import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  TextField,
  IconButton,
  Modal,
  styled,
} from '@mui/material';

// Modal Container and Structure - Shared across all steps
export const ModalContainer = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

export const ModalContent = styled(Box)(({ theme }) => ({
  width: '90%',
  maxWidth: '500px',
  maxHeight: '90vh',
  overflow: 'auto',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(3),
  position: 'relative',
  outline: 'none',
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25), 0 12px 24px rgba(0, 0, 0, 0.15)',
  border: `1px solid ${theme.palette.divider}`,
}));

export const ModalHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}));

export const ModalTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  fontSize: '17px',
  lineHeight: '22px',
  letterSpacing: '-0.408px',
  fontWeight: 600,
}));

// Navigation Buttons - Shared across all steps
export const CloseButtonContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1,
}));

export const BackButtonContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  zIndex: 1,
}));

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.colors.blue,
  '&:hover': {
    backgroundColor: 'rgba(10, 132, 255, 0.1)',
    transform: 'translateY(-1px)',
  },
}));

// Utility Containers - Shared across all steps
export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px',
}));

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

// Step Content Containers
export const StepContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

// Options Step Styles
export const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

// Selection Step Styles
export const SelectionDescription = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  opacity: 0.7,
  marginBottom: theme.spacing(3),
  fontSize: '0.875rem',
}));

export const BreaksList = styled(List)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1),
}));

export const BreakListItem = styled(ListItem)(({ theme }) => ({
  padding: 0,
  marginBottom: theme.spacing(1),
}));

export const BreakListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.secondary,
  '&:hover': {
    backgroundColor: theme.palette.background.secondaryHover,
    transform: 'translateY(-1px)',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.background.selected,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.background.secondarySelected,
    },
  },
}));

export const BreakListItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    fontWeight: 500,
    fontSize: '1rem',
  },
  '& .MuiListItemText-secondary': {
    opacity: 0.8,
    fontSize: '0.875rem',
  },
}));

// Confirmation Step Styles
export const ModalContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
}));

export const CloseButtonWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
}));

export const BreakInfoText = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  fontSize: '0.875rem',
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.colors.gray,
    },
    '&:hover fieldset': {
      borderColor: theme.colors.white,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.blue,
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.colors.gray,
    '&.Mui-focused': {
      color: theme.colors.blue,
    },
  },
}));

// Warning Styles
export const WarningContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.warning.main}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

export const WarningText = styled(Typography)(({ theme }) => ({
  color: theme.palette.warning.dark,
  fontSize: '0.875rem',
  lineHeight: 1.4,
}));

export const BreakDetailsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));
